/**
 * Stateless session tokens (HMAC-SHA256, JWT-shaped).
 *
 * WHY STATELESS: Workers KV allows 1,000 writes per day on the free plan
 * (research.md R5). A server-side session store would consume that quota at
 * roughly one write per staff sign-in and one per refresh — a live production
 * failure at a few hundred logins. A signed token costs zero writes.
 *
 * The trade-off accepted: a token cannot be revoked before it expires. Lifetimes
 * are therefore short, and the authoritative check on every mutating request is
 * the staff member's `active` flag in D1 (FR-055, FR-057) — a deactivated
 * employee is refused at the endpoint even if their token has not yet expired.
 */

export interface SessionClaims {
  /** staff id */
  sub: string;
  name: string;
  roleId: string;
  roleName: string;
  permissions: string[];
  terminalId: string | null;
  /** issued at, seconds */
  iat: number;
  /** expires at, seconds */
  exp: number;
}

export const SESSION_TTL_SECONDS = 12 * 60 * 60; // one trading day
export const AUTHORIZATION_TTL_SECONDS = 120; // single manager approval

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Throws on invalid input. Callers handling untrusted tokens MUST catch —
 * a malformed token is a 401, never a 500. Anyone can post arbitrary bytes to
 * an auth endpoint, so this path is reached by hostile input by design.
 */
function base64UrlDecode(value: string): Uint8Array {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function importSigningKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

export async function issueSession(
  claims: Omit<SessionClaims, "iat" | "exp">,
  secret: string,
  ttlSeconds: number = SESSION_TTL_SECONDS,
  now: Date = new Date(),
): Promise<string> {
  const issuedAt = Math.floor(now.getTime() / 1000);
  const payload: SessionClaims = {
    ...claims,
    iat: issuedAt,
    exp: issuedAt + ttlSeconds,
  };

  const encoder = new TextEncoder();
  const header = base64UrlEncode(encoder.encode(JSON.stringify({ alg: "HS256", typ: "JWT" })));
  const body = base64UrlEncode(encoder.encode(JSON.stringify(payload)));
  const signingInput = `${header}.${body}`;

  const key = await importSigningKey(secret);
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(signingInput));

  return `${signingInput}.${base64UrlEncode(new Uint8Array(signature))}`;
}

export type SessionResult =
  | { valid: true; claims: SessionClaims }
  | { valid: false; reason: "malformed" | "bad_signature" | "expired" };

export async function verifySession(
  token: string,
  secret: string,
  now: Date = new Date(),
): Promise<SessionResult> {
  const parts = token.split(".");
  if (parts.length !== 3) return { valid: false, reason: "malformed" };

  const [header, body, signature] = parts as [string, string, string];
  const encoder = new TextEncoder();
  const key = await importSigningKey(secret);

  let verified: boolean;
  try {
    verified = await crypto.subtle.verify(
      "HMAC",
      key,
      base64UrlDecode(signature) as BufferSource,
      encoder.encode(`${header}.${body}`),
    );
  } catch {
    // Signature segment was not valid base64url at all.
    return { valid: false, reason: "malformed" };
  }
  if (!verified) return { valid: false, reason: "bad_signature" };

  let claims: SessionClaims;
  try {
    claims = JSON.parse(new TextDecoder().decode(base64UrlDecode(body))) as SessionClaims;
  } catch {
    return { valid: false, reason: "malformed" };
  }

  if (typeof claims?.sub !== "string" || typeof claims?.exp !== "number") {
    return { valid: false, reason: "malformed" };
  }

  if (claims.exp * 1000 <= now.getTime()) return { valid: false, reason: "expired" };

  return { valid: true, claims };
}

/**
 * Offline leniency.
 *
 * A terminal with no network cannot refresh a token, and refusing to sell
 * because a signature expired mid-shift would violate Principle I outright. The
 * terminal is a registered, physically controlled device behind a counter, so
 * an expired-but-validly-signed token is honoured offline to the end of the
 * shift. Every mutation it produced is re-authorised server-side on sync.
 */
export const OFFLINE_GRACE_SECONDS = 18 * 60 * 60;

export function isAcceptableOffline(claims: SessionClaims, now: Date = new Date()): boolean {
  const seconds = Math.floor(now.getTime() / 1000);
  return seconds <= claims.exp + OFFLINE_GRACE_SECONDS;
}

export function hasPermission(claims: SessionClaims, permission: string): boolean {
  return claims.permissions.includes(permission) || claims.permissions.includes("*");
}
